/**
 * Theme Adapter
 *
 * Adapter functions to help components use the MUI theme structure.
 * Maps legacy theme names to MUI button variants and icon states.
 *
 * NOTE: This adapter now uses the consolidated MUI theme instead of Themes.js
 */

import { useTheme } from '@mui/material/styles';

/** Map of legacy button theme names to MUI button variant strings */
const BUTTON_VARIANT_MAP: Record<string, string> = {
  'nav_red': 'nav-red',
  'nav_black': 'nav-black',
  'nav_gray': 'nav-gray',
  'nav_light': 'nav-light',
  'units': 'units',
  'buttonGroupDef': 'button-group-default',
  'buttonGroupBig': 'button-group-big',
  'buttonGroupBigNav': 'button-group-big-nav',
  'furnitureGroup': 'furniture-group',
  'furnitureGroupNav': 'furniture-group-nav',
  'deviceGroup': 'device-group',
  'acousticGroup': 'acoustic-group',
  'acousticGroupNav': 'acoustic-group-nav',
  'familyGroup': 'family-group',
  'navGroup': 'nav-group',
  'actionGroup': 'action-group',
  'dotGroup': 'dot-group',
};

/**
 * Adapts legacy button theme names to MUI button variant props.
 *
 * @param legacyTheme - The legacy theme name (string) or legacy style object
 * @param additionalProps - Additional props to merge with the result
 * @returns MUI-compatible props object with variant or sx
 */
export const adaptButtonTheme = (legacyTheme: string | Record<string, any> | null | undefined, additionalProps: Record<string, any> = {}): Record<string, any> => {
  if (!legacyTheme) return additionalProps;

  // If legacyTheme is a string, map it to a variant
  if (typeof legacyTheme === 'string') {
    const variant = BUTTON_VARIANT_MAP[legacyTheme];
    if (variant) {
      return {
        ...additionalProps,
        variant,
      };
    }
  }

  // If legacyTheme is an object with theme name
  if (typeof legacyTheme === 'object' && legacyTheme.name && BUTTON_VARIANT_MAP[legacyTheme.name]) {
    return {
      ...additionalProps,
      variant: BUTTON_VARIANT_MAP[legacyTheme.name],
    };
  }

  // Fallback for direct style objects (temporary during migration)
  if (typeof legacyTheme === 'object') {
    return {
      ...additionalProps,
      sx: {
        backgroundColor: legacyTheme.background || 'transparent',
        color: legacyTheme.color || 'inherit',
        fontWeight: 'bold',
        fontSize: legacyTheme.size,
        border: legacyTheme.border,
        borderRight: legacyTheme.borderRight,
        borderRadius: legacyTheme.radius,
        height: legacyTheme.maxHeight || '100%',
        minHeight: legacyTheme.maxHeight || '30px',
        width: legacyTheme.width,
        padding: legacyTheme.padding,
        maxWidth: legacyTheme.maxWidth,
        justifyContent: legacyTheme.justify || 'center',
        '&:hover': {
          maxWidth: additionalProps.editable ? 'fit-content' : undefined,
          '& .text-truncate': {
            overflow: 'visible',
            textOverflow: 'unset',
            whiteSpace: 'unset',
          }
        },
        ...(additionalProps.sx || {}),
      },
    };
  }

  return additionalProps;
};

/**
 * Hook to get icon state styles from theme.
 *
 * @param stateName - The name of the icon state
 * @returns Icon state sx styles
 */
export const useIconTheme = (stateName: string): Record<string, any> => {
  const theme = useTheme();
  const state = (theme as any).custom?.iconStates?.[stateName];

  if (!state) return {};

  return {
    sx: {
      '& .border': {
        stroke: state.borderStroke,
        fill: state.borderFill,
      },
      '& .icon': {
        stroke: state.iconStroke,
        fill: state.iconFill,
      },
    }
  };
};

/**
 * Adapts legacy icon theme state names to use theme icon states.
 * For backward compatibility during migration — components should migrate to useIconTheme.
 *
 * @param stateName - The name of the icon state
 * @returns MUI-compatible props object for icons
 */
export const adaptIconTheme = (_stateName: string): Record<string, any> => {
  return {};
};

/**
 * Gets a button variant name from a legacy theme name.
 *
 * @param themeName - The legacy theme name
 * @returns The MUI button variant name, or 'text' as fallback
 */
export const getButtonVariant = (themeName: string): string => {
  return BUTTON_VARIANT_MAP[themeName] || 'text';
};

/**
 * @deprecated Use MUI button variant prop instead.
 */
export const getButtonTheme = (themeName: string): Record<string, any> => {
  console.warn(`getButtonTheme is deprecated. Use MUI button variant="${BUTTON_VARIANT_MAP[themeName]}" instead`);
  return {};
};

/**
 * @deprecated Use useIconTheme hook instead.
 */
export const getIconTheme = (_stateName: string): Record<string, any> => {
  console.warn('getIconTheme is deprecated. Use useIconTheme hook instead');
  return {};
};
